import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Match, Player } from "../../types";
import { Activity, Clock } from "../../../../components/icons";
import TeamLogo from "../../../../components/TeamLogo";

const formatPercentOneDecimal = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);

interface LiveMatchContextBannerProps {
  match: Match;
  onFieldHomePlayers: Player[];
  onFieldAwayPlayers: Player[];
  effectiveTime: number;
  ineffectiveSeconds: number;
  /**
   * Optional live clock mode. When "INEFFECTIVE", the ineffective-time value
   * also carries `data-testid="clock-mode-ineffective"` — the public share
   * page's sole surface for this mode indicator now that its own inline
   * clock strip has been removed (see PublicMatchAnalytics.tsx). Omitted by
   * the cockpit caller (AnalyticsView), which has no equivalent indicator.
   */
  clockMode?: "EFFECTIVE" | "INEFFECTIVE";
  t: (key: string, fallback: string) => string;
}

const formatSecondsAsClock = (seconds: number) => {
  const safeSeconds = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  const mins = Math.floor(safeSeconds / 60);
  const secs = Math.floor(safeSeconds % 60);
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const parseBirthDate = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const ageInYears = (birthDate: Date, atDate: Date = new Date()) => {
  let age = atDate.getFullYear() - birthDate.getFullYear();
  const m = atDate.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && atDate.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age;
};

const calculateOnFieldAverageAge = (players: Player[]): number | null => {
  const ages = players
    .map((p) => parseBirthDate(p.birth_date))
    .filter((d): d is Date => d !== null)
    .map((d) => ageInYears(d))
    .filter((a) => Number.isFinite(a) && a > 0);
  if (!ages.length) return null;
  return ages.reduce((s, a) => s + a, 0) / ages.length;
};

export default function LiveMatchContextBanner({
  match,
  onFieldHomePlayers,
  onFieldAwayPlayers,
  effectiveTime,
  ineffectiveSeconds,
  clockMode,
  t,
}: LiveMatchContextBannerProps) {
  const { i18n } = useTranslation();
  const homeAge = useMemo(
    () => calculateOnFieldAverageAge(onFieldHomePlayers),
    [onFieldHomePlayers],
  );
  const awayAge = useMemo(
    () => calculateOnFieldAverageAge(onFieldAwayPlayers),
    [onFieldAwayPlayers],
  );
  const na = t("analytics.notAvailable", "N/A");

  return (
    <div
      data-testid="live-match-context"
      className="rounded-xl border border-purple-700/40 bg-gradient-to-r from-purple-900/60 via-slate-900/80 to-purple-900/60 px-4 py-3 shadow-lg"
    >
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wider text-purple-300">
        <Activity size={14} />
        {t("analytics.liveMatchContext", "Live Match Context")}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Global Effective Time */}
        <div
          data-testid="stat-total-effective-time"
          className="flex flex-col items-center rounded-lg bg-white/5 px-3 py-2"
        >
          <span className="text-[10px] uppercase tracking-wide text-emerald-400 flex items-center gap-1">
            <Clock size={12} />
            {t("analytics.totalEffectiveTime", "Total Effective Time")}
          </span>
          <span className="mt-0.5 font-mono text-lg font-bold text-emerald-300">
            {formatSecondsAsClock(effectiveTime)}
          </span>
          {(effectiveTime > 0 || ineffectiveSeconds > 0) && (
            <span className="text-base font-mono font-semibold text-emerald-400/80">
              {formatPercentOneDecimal(
                (effectiveTime / (effectiveTime + ineffectiveSeconds)) * 100,
                i18n.language,
              )}
              %
            </span>
          )}
        </div>

        {/* Global Ineffective Time */}
        <div
          data-testid="stat-total-ineffective-time"
          className="flex flex-col items-center rounded-lg bg-white/5 px-3 py-2"
        >
          <span className="text-[10px] uppercase tracking-wide text-rose-400 flex items-center gap-1">
            <Clock size={12} />
            {t("analytics.totalIneffectiveTime", "Total Ineffective Time")}
          </span>
          <span
            data-testid={
              clockMode === "INEFFECTIVE" ? "clock-mode-ineffective" : undefined
            }
            className="mt-0.5 font-mono text-lg font-bold text-rose-300"
          >
            {formatSecondsAsClock(ineffectiveSeconds)}
          </span>
          {(effectiveTime > 0 || ineffectiveSeconds > 0) && (
            <span className="text-base font-mono font-semibold text-rose-400/80">
              {formatPercentOneDecimal(
                (ineffectiveSeconds / (effectiveTime + ineffectiveSeconds)) *
                  100,
                i18n.language,
              )}
              %
            </span>
          )}
        </div>

        {/* On-Field Average Age — Home */}
        <div
          data-testid="stat-on-field-age-home"
          className="flex flex-col items-center rounded-lg bg-white/5 px-3 py-2"
        >
          <span className="text-[10px] uppercase tracking-wide text-sky-400 flex items-center gap-1">
            <TeamLogo team={match.home_team} size="sm" bare />
            {match.home_team.short_name}{" "}
            {t("analytics.onFieldAvgAge", "Avg Age")}
          </span>
          <span className="mt-0.5 font-mono text-lg font-bold text-sky-300">
            {homeAge !== null ? homeAge.toFixed(1) : na}
          </span>
        </div>

        {/* On-Field Average Age — Away */}
        <div
          data-testid="stat-on-field-age-away"
          className="flex flex-col items-center rounded-lg bg-white/5 px-3 py-2"
        >
          <span className="text-[10px] uppercase tracking-wide text-amber-400 flex items-center gap-1">
            <TeamLogo team={match.away_team} size="sm" bare />
            {match.away_team.short_name}{" "}
            {t("analytics.onFieldAvgAge", "Avg Age")}
          </span>
          <span className="mt-0.5 font-mono text-lg font-bold text-amber-300">
            {awayAge !== null ? awayAge.toFixed(1) : na}
          </span>
        </div>
      </div>
    </div>
  );
}
